import { Stack, StackProps, Text, isWeb, useMedia } from 'tamagui'
import { useEffect, useState } from 'react'
import { HeroIcon } from '@components/HeroIcon'
import debounce from 'lodash/debounce'

interface INavigationControlProps extends StackProps {
  action: () => void
  chevronDirection: 'left' | 'right'
  size: number
  isCarousel?: boolean
  disabled?: boolean
  chevronColor?: string
  label?: string
}

export function NavigationControl({
  action,
  chevronDirection,
  size,
  isCarousel = false,
  disabled = false,
  chevronColor = '$icon-secondary',
  label,
  ...extraProps
}: INavigationControlProps) {
  const [isActive, setIsActive] = useState(false)
  const media = useMedia()
  const handleClick = debounce(action, 300)
  const buttonSize = media.gtMd ? 52 : 32
  const iconColor = isActive && !disabled ? '$accent' : chevronColor
  const cursorPointer = disabled ? 'default' : 'pointer'
  const backgroundColor =
    disabled || isCarousel || !isActive ? 'transparent' : '$bg-surface-pressed'

  const setPadding = () => {
    if (isCarousel) return 4
    return media.gtMd ? 12 : 8
  }

  useEffect(() => {
    setIsActive(false)
  }, [disabled])

  const chevron = (
    <HeroIcon
      icon={['fal', `chevron-${chevronDirection}`]}
      size={size}
      style={{
        opacity: disabled ? 0.5 : 1,
      }}
      color={iconColor}
    />
  )

  const labelText = label ? (
    <Text
      fontSize="$font-size-sm"
      fontFamily="$mulishRegular"
      lineHeight={24}
      color={disabled ? '$text-placeholder' : '$text-primary'}
    >
      {label}
    </Text>
  ) : null

  return (
    <Stack
      onPress={() => !disabled && handleClick()}
      height={buttonSize}
      width={label ? 'auto' : buttonSize}
      borderRadius={'$border-radius-md'}
      disabled={disabled}
      padding={setPadding()}
      flexDirection="row"
      gap={label ? '$spacing-inset-nano' : 0}
      justifyContent="center"
      alignItems="center"
      cursor={cursorPointer}
      backgroundColor={backgroundColor}
      focusStyle={{ outlineWidth: 0 }}
      onFocus={() => (isCarousel ? setIsActive(true) : null)}
      onBlur={() => (isCarousel ? setIsActive(false) : null)}
      onPressIn={() => !disabled && setIsActive(true)}
      onPressOut={() => setIsActive(false)}
      onHoverIn={() => isWeb && !disabled && setIsActive(true)}
      onHoverOut={() => isWeb && setIsActive(false)}
      hoverStyle={{
        backgroundColor: !disabled ? backgroundColor : 'transparent',
      }}
      {...extraProps}
    >
      {chevronDirection === 'left' ? (
        <>
          {chevron}
          {labelText}
        </>
      ) : (
        <>
          {labelText}
          {chevron}
        </>
      )}
    </Stack>
  )
}
